/**
 * Profile / subscription screen icons — single-color glyphs painted with
 * `currentColor`, sized by the wrapping `<Icon>` primitive. Chunky-rounded
 * strokes (w=2.5, round caps) so they sit next to the bold-pass borders
 * without looking thin. Distinct from nav/icons.tsx, which are the LARGE
 * two-tone tab glyphs.
 */

const S = {
  width: '100%',
  height: '100%',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export function FlameGlyph() {
  return (
    <svg {...S}>
      <path d="M12 3c.5 3-1.6 4.6-2.9 6.3A6.3 6.3 0 0 0 12 21a6.3 6.3 0 0 0 6.3-6.3c0-2.6-1.6-4.2-2.6-5.4-.4 1-.7 2.2-2 2.7C13.4 9.6 14.5 6 12 3Z" />
    </svg>
  );
}

export function StarGlyph() {
  return (
    <svg {...S}>
      <path d="m12 3 2.7 5.5 6 .9-4.3 4.2 1 6-5.4-2.8-5.4 2.8 1-6L3.3 9.4l6-.9L12 3Z" />
    </svg>
  );
}

export function BookGlyph() {
  return (
    <svg {...S}>
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5H6.5A2.5 2.5 0 0 0 4 21V5.5Z" />
      <path d="M4 18.5A2.5 2.5 0 0 1 6.5 16H20" />
    </svg>
  );
}

export function CheckCircleGlyph() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </svg>
  );
}

export function TrophyGlyph() {
  return (
    <svg {...S}>
      <path d="M8 21h8M12 17v4M7 4h10v6a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
    </svg>
  );
}

export function GearGlyph() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2 5.5 5.5" />
    </svg>
  );
}

export function PhoneGlyph() {
  return (
    <svg {...S}>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </svg>
  );
}

export function ShieldGlyph() {
  return (
    <svg {...S}>
      <path d="M12 3 5 5.5V11c0 4.5 2.8 8 7 10 4.2-2 7-5.5 7-10V5.5L12 3Z" />
    </svg>
  );
}

export function PencilGlyph() {
  return (
    <svg {...S}>
      <path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1Z" />
      <path d="m14.5 6.5 3 3" />
    </svg>
  );
}

export function CrownGlyph() {
  return (
    <svg {...S}>
      <path d="M4 8.5 7.5 12 12 5.5 16.5 12 20 8.5V17a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8.5Z" />
    </svg>
  );
}

export function CardGlyph() {
  return (
    <svg {...S}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19M6 15h4" />
    </svg>
  );
}

export function LockGlyph() {
  return (
    <svg {...S}>
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function CalendarGlyph() {
  return (
    <svg {...S}>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M3.5 10h17M8 2.8V6.5M16 2.8V6.5" />
    </svg>
  );
}

export function RefreshGlyph() {
  return (
    <svg {...S}>
      <path d="M20 12a8 8 0 1 1-2.3-5.6M20 3.5V8h-4.5" />
    </svg>
  );
}

export function LogoutGlyph() {
  return (
    <svg {...S}>
      <path d="M14 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7M10 12h11M17.5 8.5 21 12l-3.5 3.5" />
    </svg>
  );
}

export function CoinGlyph() {
  // A coin stamped with a simplified lari (₾) — coins are tetri, not
  // dollars; the currency mark must match.
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M10.6 7.6v5.2a3 3 0 0 0 2.8 0V7.6M8.2 16h7.6" />
    </svg>
  );
}

export function TargetGlyph() {
  return (
    <svg {...S}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    </svg>
  );
}
